import React, { useEffect } from 'react';
import { EditorContent, useEditor, useEditorState, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  List,
  ListOrdered,
  Pilcrow,
  Redo2,
  Undo2,
} from 'lucide-react';
import './aboutUsEditor.css';

/**
 * The mobile app renders About Us through flutter_html, which is styled for
 * p, h1, h2, h3, ul and li only (lyfe_app app_info_widgets.dart). Anything
 * richer would either render unstyled or, in the case of links, render as
 * tappable text with no tap handler attached. So the editor is deliberately
 * limited to marks the app displays correctly — the admin cannot author markup
 * the app cannot show.
 */
const EDITOR_EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    codeBlock: false,
    code: false,
    blockquote: false,
    horizontalRule: false,
    strike: false,
    underline: false,
    link: false,
  }),
];

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

const ToolbarButton = ({
  onClick,
  isActive = false,
  disabled = false,
  title,
  children,
}: ToolbarButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    aria-pressed={isActive}
    className={`p-2 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
      isActive
        ? 'bg-custom-500 text-white'
        : 'text-slate-600 dark:text-zink-200 hover:bg-slate-100 dark:hover:bg-zink-600'
    }`}
  >
    {children}
  </button>
);

const ToolbarDivider = () => (
  <span className="w-px h-6 bg-slate-200 dark:bg-zink-500 mx-1" />
);

interface AboutUsEditorToolbarProps {
  editor: Editor;
}

const AboutUsEditorToolbar = ({ editor }: AboutUsEditorToolbarProps) => {
  // TipTap v3 does not re-render on every transaction by default, so the
  // active/disabled states have to be pulled through a selector to stay in
  // sync with the cursor.
  const state = useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      isParagraph: instance.isActive('paragraph'),
      isHeading1: instance.isActive('heading', { level: 1 }),
      isHeading2: instance.isActive('heading', { level: 2 }),
      isHeading3: instance.isActive('heading', { level: 3 }),
      isBold: instance.isActive('bold'),
      isItalic: instance.isActive('italic'),
      isBulletList: instance.isActive('bulletList'),
      isOrderedList: instance.isActive('orderedList'),
      canUndo: instance.can().undo(),
      canRedo: instance.can().redo(),
    }),
  });

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b border-slate-200 dark:border-zink-500">
      <ToolbarButton
        title="Paragraph"
        isActive={state.isParagraph}
        onClick={() => editor.chain().focus().setParagraph().run()}
      >
        <Pilcrow className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Heading 1"
        isActive={state.isHeading1}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Heading 2"
        isActive={state.isHeading2}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Heading 3"
        isActive={state.isHeading3}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="size-4" />
      </ToolbarButton>

      <ToolbarDivider />

      <ToolbarButton
        title="Bold"
        isActive={state.isBold}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        isActive={state.isItalic}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="size-4" />
      </ToolbarButton>

      <ToolbarDivider />

      <ToolbarButton
        title="Bulleted list"
        isActive={state.isBulletList}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Numbered list"
        isActive={state.isOrderedList}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="size-4" />
      </ToolbarButton>

      <ToolbarDivider />

      <ToolbarButton
        title="Undo"
        disabled={!state.canUndo}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 className="size-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Redo"
        disabled={!state.canRedo}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 className="size-4" />
      </ToolbarButton>
    </div>
  );
};

interface AboutUsEditorProps {
  /** Initial HTML. Applied on mount and whenever the identity changes. */
  content: string;
  /** Bumped by the parent when the body is replaced from outside the editor. */
  contentKey: string;
  onChange: (html: string) => void;
  disabled?: boolean;
}

const AboutUsEditor = ({
  content,
  contentKey,
  onChange,
  disabled = false,
}: AboutUsEditorProps) => {
  const editor = useEditor({
    extensions: EDITOR_EXTENSIONS,
    content,
    editable: !disabled,
    editorProps: {
      attributes: {
        // Matches the scroll container's height so clicking anywhere in the
        // box puts the cursor in the document, not just on the text.
        class: 'about-rich-text min-h-[420px] px-4 py-3',
        'data-placeholder': 'Write the About Us content…',
      },
    },
    onUpdate: ({ editor: instance }) => onChange(instance.getHTML()),
  });

  // Re-seed only when the parent signals a fresh document. Keying on contentKey
  // rather than content keeps the editor from resetting the cursor on every
  // keystroke, since each keystroke feeds `content` back through onChange.
  useEffect(() => {
    if (!editor) return;
    editor.commands.setContent(content, { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, contentKey]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return null;

  return (
    <div className="border border-slate-200 dark:border-zink-500 rounded-md overflow-hidden">
      <AboutUsEditorToolbar editor={editor} />
      {/* The body is capped rather than left to grow, so a long document
          scrolls inside the card instead of pushing the toolbar and the save
          controls off the top of the page. */}
      <div className="h-[420px] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default AboutUsEditor;
